import { IAnalyticsServiceConfiguration } from '..'
import { IDate, IMain, ITitle } from '../../../domain/conf/page'
import { Utils } from '../../../utils/common'
import { DateFormat, DateUtils } from '../../../utils/date'
import { KlangooUtils } from '../../../utils/klangoo'
import { IKlangooNLPConfig } from '../../common/nlp/klangoo-config'
import { PageType } from '../../common/page-types'
import { AnalyticsMetaKeys as key } from './meta-keys'

const NLP_ENTITY_SCORE_THRESHOLD = .25
const NLP_ENTITY_RANK = ['R', 'VR']
const NULL_SUB_SEC = 'NULL'
const DEFAULT_PAGE_STATUS = 200

export interface IAnalyticsMetaFormatter {
    meta(conf: IAnalyticsServiceConfiguration): any
}

export class DefaultAnalyticsFormatter implements IAnalyticsMetaFormatter {
    public static dateFormat = DateFormat.YYYY_MM_DD
    public static timeFormat = DateFormat.HH_MM_SS
    public static supportedPageTypes = [PageType.CATEGORY, PageType.INDEX, PageType.STORY]

    // @todo - simplify:
    // tslint:disable-next-line: cognitive-complexity
    public meta(conf: IAnalyticsServiceConfiguration) {
        const meta: any = {
            // user
            [key.USER_LOGGEDIN]: String(!!conf.profile.email),
            // ads
            [key.AD_SITE]: conf.adSite,
            [key.AD_LIGHT]: String(conf.profile.adLight),
            // analytics
            [key.BRAND]: conf.brand,
            [key.DIVISION]: conf.division,
            [key.DOMAIN]: conf.domain,
            [key.VENDOR]: conf.vendor,
            [key.DEBUG]: conf.debug,
            // page
            [key.TYPE]: conf.page.type,
            [key.PAYWALL]: String(conf.page.isPaywallWhitelisted),
            [key.METERED_CONTENT_TYPE]: this.paywallText(conf.page.paywall)
        }

        if (conf.page.articleId) {
            meta[key.ARTICLE_ID] = conf.page.articleId
        }
        if (conf.page.originId) {
            meta[key.ORIGINAL_ID] = conf.page.originId
        }
        if (conf.page.dist) {
            meta[key.DISTRIBUTOR] = conf.page.dist
        }
        if (conf.page.error) {
            meta[key.PAGE_RES_TYPE] = String(conf.page.error)
            meta[key.ERROR] = String(conf.page.error).match(/20[01]/) ? String(false) : String(true)
        } else {
            meta[key.PAGE_RES_TYPE] = String(DEFAULT_PAGE_STATUS)
            meta[key.ERROR] = String(false)
        }
        if (conf.page.excerpt) {
            meta[key.EXCERPT] = conf.page.excerpt
        }
        if (String(conf.page.isOrigin)) {
            meta[key.IS_ORIGINAL] = String(conf.page.isOrigin)
        }
        if (conf.page.video && String(conf.page.video.inPage)) {
            meta[key.VID_IN_PAGE] = String(conf.page.video.inPage)
        }
        if (conf.page.wc) {
            meta[key.WRD_CNT] = String(conf.page.wc)
        }
        if (conf.page.writers && conf.page.writers.main && conf.page.writers.main.length > 0) {
            meta[key.WRITERS] = conf.page.writers.main.map((_) => typeof _ === 'object' ? _.name : _).join(', ')
        }
        if (conf.page.sponsor) {
            meta[key.SPONSOR] = conf.page.sponsor
        }
        return {
            ...meta,
            ...this.isWire(conf.page),
            ...this.tag(conf.page),
            ...this.title(conf.page.title),
            ...this.category(conf.page.ancestry, conf.page.type),
            ...this.dateFormat(conf.page.date),
            ...this.klangoo(conf.page.klangoo),
            ...this.writers(conf.page)
        }
    }

    // tag and kw
    public tag(data: any): object {
        const meta: any = {}
        if (data.kw) {
            meta[key.NEWS_KEYWRD] = data.kw.join(', ')
        }
        if (data.tags) {
            meta[key.META_TAG] = data.tags.join(', ')
        }
        if (data.globalSlug) {
            meta[key.SLUG] = String(data.globalSlug)
        }
        if (data.projectType) {
            meta[key.PROJECT_TYPE] = String(data.projectType)
        }
        return meta
    }

    // writers, byline, wire authors
    public writers(data: any): object {
        const meta: any = {}

        if (data && data.writers) {
            if (data.writers.main && data.writers.main.length > 0) {
                meta[key.WRITERS] = data.writers.main.map((_: IMain) => typeof _ === 'object' ? _.name : _).join('|')
            }
            if (data.writers.wire && data.writers.wire.length > 0) {
                meta[key.WIRE_AUTHORS] = data.writers.wire.join('|')
            }
            if (data.writers.byline) {
                meta[key.BYLINE] = data.writers.byline
            }
        }

        return meta
    }

    // wireId
    public isWire(data: any): object {
        const meta: any = {}
        if (data) {
            if (data.wireId) {
                meta[key.WIRE_ID] = data.wireId
            }
            if ((data.writers && data.writers.wire && data.writers.wire.length > 0) || data.wireId) {
                meta[key.IS_WIRE] = String(true)
            } else {
                meta[key.IS_WIRE] = String(false)
            }
        }

        return meta
    }

    // Title
    public title(data: ITitle): object {
        const meta: any = {}
        if (data) {
            if (data.main) {
                meta[key.TITLE] = Utils.decodeHtmlEntity(data.main)
            }
            if (data.alt) {
                meta[key.HEADLINE] = Utils.decodeHtmlEntity(data.alt)
            }
        }
        return meta
    }

    // Dates
    public dateFormat(data: IDate): object {
        const meta: any = {}
        if (data) {
            if (data.pub) {
                meta[key.PUB_DATE] = DateUtils.format(
                    data.pub, DefaultAnalyticsFormatter.dateFormat)
                meta[key.PUB_TIME] = DateUtils.format(
                    data.pub, DefaultAnalyticsFormatter.timeFormat)
            }
            if (data.mod) {
                meta[key.MOD_DATE] = DateUtils.format(
                    data.mod, DefaultAnalyticsFormatter.dateFormat)
                meta[key.MOD_TIME] = DateUtils.format(
                    data.mod, DefaultAnalyticsFormatter.timeFormat)
            }
        }
        return meta
    }

    // Category
    public category(ancestry: string[], type: PageType): object {
        const data =
            // analytics expects ancestry of 'index' on home page:
            type === PageType.INDEX && !(ancestry && ancestry.length) ? ['index']
            : [...(ancestry || [])]
        const meta: { [_: string]: string } = {}
        if (data.length) {
            meta[key.ALL_SUB_SEC] = data.join('/')
            meta[key.MAIN_CATEGORY] = data[data.length - 1]
            meta[key.CATEGORY] = data.shift()

            if (data.length > 0) {
                meta[key.SUB_SEC] = data.shift()
            } else {
                meta[key.SUB_SEC] = NULL_SUB_SEC
            }
            if (data.length > 0) {
                meta[key.SUB_SEC_2] = data.shift()
            } else {
                meta[key.SUB_SEC_2] = NULL_SUB_SEC
            }
            if (data.length > 0) {
                meta[key.SUB_SEC_3] = data.shift()
            } else {
                meta[key.SUB_SEC_3] = NULL_SUB_SEC
            }
        }
        return meta
    }

    // Kalngoo
    public klangoo(data: IKlangooNLPConfig): object {
        const meta: any = {}
        if (data && data.entities) {
            meta[key.KLNGO_ENTITY] =
                [...new Set(
                    [...KlangooUtils.stringifyFilteredEntities(data.entities,
                        NLP_ENTITY_RANK, NLP_ENTITY_SCORE_THRESHOLD),
                    ...KlangooUtils.stringifyFilteredKeytopics(data.keyTopics,
                        NLP_ENTITY_SCORE_THRESHOLD),
                    ...KlangooUtils.stringifyFilteredCategories(data.categories,
                        NLP_ENTITY_SCORE_THRESHOLD)]
                )].join('| ')

            // JIRA #AA-188 New requirement for NLP Entity and KeyTopics
            meta[key.NLP_ENTITY] = KlangooUtils.stringifyFilteredEntities(data.entities).join('| ')
            meta[key.NLP_TOPIC] = KlangooUtils.stringifyFilteredKeytopics(data.keyTopics).join('| ')
        }
        if (data && data.categories) {
            meta[key.KLNGO_CATEGORY] = KlangooUtils.stringifyFilteredCategories(data.categories,
                NLP_ENTITY_SCORE_THRESHOLD).join('| ')
        }
        return meta
    }

    public paywallText(paywall: string): string {
        if (paywall) {
            if (paywall.length < 1) {
                return ''
            }
            const paywallText: any = {
                ccate: 'Commercial Category',
                exclusive: 'Exclusive',
                fwpost: 'Full Width Post',
                hflist: 'Hide Post From List',
                nofollow: 'No Follow',
                noindex: 'No Index',
                subscriber: 'Subscriber Only',
                whitelist: 'Paywall Whitelist',
            }
            const textArr: string[] = []
            paywall.split(', ').forEach((text: any) => {
                if (paywallText[text]) {
                    textArr.push(paywallText[text])
                } else {
                    textArr.push('Metered Content')
                }
            })
            return textArr.join(', ')
        }
        return ''
    }
}
